import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../shared/lib/supabase'
import { useAuth } from './AuthContext'
import { useOrg } from './OrgContext'
import { splitLegacyIndustry, getDefaultFrameworks, getDefaultDataTypes } from '../shared/data/industries'
import { recommendProfile } from '../shared/utils/profileRecommendation'
import { logAuditEvent } from '../shared/lib/auditLog'
import { getMaturityLevelByScore, MATURITY_LEVELS } from '../shared/utils/maturityLevels'

// The DB stores maturity_level as a plain string label (e.g. 'Developing').
// Consumers expect a hydrated object with label/level/color/range. This
// resolves the string back to the canonical level object, falling back
// to score-based lookup if the label is missing or unrecognized.
function hydrateMaturityLevel(label, score) {
  if (label) {
    const match = MATURITY_LEVELS.find((l) => l.label === label)
    if (match) return match
  }
  if (typeof score === 'number') return getMaturityLevelByScore(score)
  return null
}

const AssessmentContext = createContext(null)

const initialState = {
  clients: [],
  currentAssessment: null,
  loading: true,
  error: null
}

function assessmentReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }

    case 'LOAD_CLIENTS':
      return { ...state, clients: action.payload, loading: false }

    case 'ADD_CLIENT':
      return { ...state, clients: [...state.clients, action.payload] }

    case 'UPDATE_CLIENT':
      return {
        ...state,
        clients: state.clients.map(c =>
          c.id === action.payload.id ? action.payload : c
        )
      }

    case 'DELETE_CLIENT':
      return {
        ...state,
        clients: state.clients.filter(c => c.id !== action.payload)
      }

    case 'SET_CURRENT_ASSESSMENT':
      // Safety: If passed an assessment with ID but no responses, look up full data from clients
      if (action.payload?.id && !action.payload?.responses) {
        for (const client of state.clients) {
          const fullAssessment = client.assessments?.find(a => a.id === action.payload.id)
          if (fullAssessment) {
            return { ...state, currentAssessment: fullAssessment }
          }
        }
      }
      return { ...state, currentAssessment: action.payload }

    case 'UPDATE_CURRENT_ASSESSMENT':
      return {
        ...state,
        currentAssessment: state.currentAssessment
          ? { ...state.currentAssessment, ...action.payload }
          : null
      }

    case 'CLEAR_DATA':
      return { ...initialState, loading: false }

    default:
      return state
  }
}

export function AssessmentProvider({ children }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState)
  const { user, profile, loading: authLoading } = useAuth()
  const { currentOrg, userOrgRole, loading: orgLoading } = useOrg()
  const lastFetchedUserId = useRef(null)
  const lastFetchedOrgId = useRef(null)
  const fetchInProgress = useRef(false)
  const retryTimeoutRef = useRef(null)

  // Fetch clients when user changes
  const fetchClients = useCallback(async (retryCount = 0) => {
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    // Wait for auth and org context to finish loading before attempting fetch
    if (authLoading || orgLoading) {
      return
    }

    if (!user) {
      lastFetchedUserId.current = null
      dispatch({ type: 'CLEAR_DATA' })
      return
    }

    // Non-client users MUST have an org selected before fetching — prevents
    // global admins from seeing all clients across orgs during initial load
    if (profile?.role !== 'client' && !currentOrg?.id) {
      return
    }

    // Prevent duplicate fetches for the same user+org (e.g., on page refresh)
    if (lastFetchedUserId.current === user.id && lastFetchedOrgId.current === (currentOrg?.id || null)) {
      // Data already loaded for this user+org, ensure loading is false
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    // On retries (not the first attempt), validate the session server-side before re-fetching.
    // Skip on first attempt — the session is likely valid (e.g., fresh login) and the extra
    // round-trip to getUser() adds unnecessary latency. If the data fetch fails, we retry here.
    if (retryCount > 0 && retryCount < 5) {
      try {
        const getUserPromise = supabase.auth.getUser()
        const getUserTimeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getUser timeout')), 10000)
        )
        const { data: { user: validatedUser }, error: userError } = await Promise.race([
          getUserPromise,
          getUserTimeoutPromise
        ])
        if (userError || !validatedUser) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
          retryTimeoutRef.current = setTimeout(() => fetchClients(retryCount + 1), delay)
          return
        }
      } catch (err) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000)
        retryTimeoutRef.current = setTimeout(() => fetchClients(retryCount + 1), delay)
        return
      }
    }

    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      return
    }

    fetchInProgress.current = true
    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      // Fetch clients with their assessments (with timeout to prevent hanging)
      // Filter by current org when available (global admins see all orgs, must scope to active one)
      let query = supabase
        .from('clients')
        .select(`
          *,
          assessments (
            *,
            assessment_scores (*)
          )
        `)
      if (currentOrg?.id) {
        query = query.eq('org_id', currentOrg.id)
      }

      // org_member users only see clients they're assigned to
      if (userOrgRole === 'org_member' && user?.id) {
        const { data: assignedClients } = await supabase
          .from('org_member_clients')
          .select('client_id')
          .eq('user_id', user.id)

        const assignedIds = (assignedClients || []).map(ac => ac.client_id)
        if (assignedIds.length > 0) {
          query = query.in('id', assignedIds)
        } else {
          // No assigned clients — return empty
          dispatch({ type: 'FETCH_CLIENTS_SUCCESS', payload: [] })
          return
        }
      }

      const fetchPromise = query.order('created_at', { ascending: false })

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Client fetch timeout')), 15000)
      )

      const { data: clients, error } = await Promise.race([fetchPromise, timeoutPromise])

      if (error) throw error

      // Collect all assessment IDs to fetch responses separately
      const assessmentIds = []
      for (const client of clients || []) {
        for (const assessment of client.assessments || []) {
          assessmentIds.push(assessment.id)
        }
      }

      // Fetch assessment_responses separately to avoid nested RLS issues (with timeout)
      let responsesMap = {}
      if (assessmentIds.length > 0) {
        try {
          const responsesPromise = supabase
            .from('assessment_responses')
            .select('*')
            .in('assessment_id', assessmentIds)

          const responsesTimeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Responses fetch timeout')), 10000)
          )

          const { data: responses, error: responsesError } = await Promise.race([
            responsesPromise,
            responsesTimeoutPromise
          ])

          if (responsesError) {
            console.error('[AssessmentContext] Error fetching responses:', responsesError)
          } else {
            // Build a map of assessment_id -> response
            for (const response of responses || []) {
              responsesMap[response.assessment_id] = response
            }
          }
        } catch (responseErr) {
          console.warn('[AssessmentContext] Responses fetch failed/timed out:', responseErr.message)
          // Continue without responses - assessments will still load
        }
      }

      // Transform the data to match our frontend structure
      const transformedClients = clients.map(client => {
        // Handle legacy composite industry values (e.g., 'Financial Services - Banking')
        // If industry_sub_category exists, use it directly; otherwise check for legacy format
        let industry = client.industry
        let industrySubCategory = client.industry_sub_category || null

        if (!industrySubCategory && industry) {
          const split = splitLegacyIndustry(industry)
          if (split.subCategory) {
            industry = split.industry
            industrySubCategory = split.subCategory
          }
        }

        return {
        id: client.id,
        name: client.name,
        industry: industry,
        industrySubCategory: industrySubCategory,
        status: client.status || 'discovery',
        contactName: client.contact_name,
        contactEmail: client.contact_email,
        secondaryContactName: client.secondary_contact_name,
        secondaryContactEmail: client.secondary_contact_email,
        notes: client.notes,
        // Business context fields
        staffingSize: client.staffing_size,
        annualRevenue: client.annual_revenue,
        itTeamSize: client.it_team_size,
        geographicalPresence: client.geographical_presence,
        portalSettings: client.portal_settings || {
          showOverview: false,
          showResults: true,
          showMaesterReport: true,
          showDocuments: false
        },
        regulatoryFrameworks: client.regulatory_frameworks || [],
        logoUrl: client.logo_url || null,
        logoInitials: client.logo_initials || null,
        logoColor: client.logo_color || null,
        domain: client.domain || null,
        enrichmentContext: client.enrichment_context || null,
        createdAt: client.created_at,
        assessments: (client.assessments || []).map(assessment => {
          // Get response from the separately fetched responsesMap
          const responseRecord = responsesMap[assessment.id]

          return {
            id: assessment.id,
            clientId: assessment.client_id,
            title: assessment.title,
            status: assessment.status,
            currentStep: assessment.current_step,
            currentSection: assessment.current_section_index,
            createdAt: assessment.created_at,
            endDate: assessment.end_date,
            completedAt: assessment.completed_at,
            responses: responseRecord ? {
              riskAppetite: responseRecord.risk_appetite || {},
              businessScoping: responseRecord.business_scoping || {},
              technologyScoping: responseRecord.technology_scoping || {},
              maturityAssessment: responseRecord.maturity_assessment || {},
              notes: responseRecord.notes || {},
              // TIDA data (stored in notes JSONB as sub-keys)
              threatContext: responseRecord.notes?.threatContext || null,
              impactData: responseRecord.notes?.impactData || null
            } : {
              riskAppetite: {},
              businessScoping: {},
              technologyScoping: {},
              maturityAssessment: {},
              notes: {},
              threatContext: null,
              impactData: null
            },
            scores: (() => {
              // Supabase returns object (unique FK) or array depending on schema
              const scoreData = Array.isArray(assessment.assessment_scores)
                ? assessment.assessment_scores[0]
                : assessment.assessment_scores
              if (!scoreData?.overall_score && scoreData?.overall_score !== 0) return null
              return {
                overall: {
                  score: scoreData.overall_score,
                  maturityLevel: hydrateMaturityLevel(scoreData.maturity_level, scoreData.overall_score)
                },
                maturity: {
                  categoryScores: scoreData.category_scores
                },
                riskAppetite: scoreData.risk_appetite_scores,
                businessScoping: scoreData.business_scoping_scores
              }
            })()
          }
        })
      }
      })

      lastFetchedUserId.current = user.id
      lastFetchedOrgId.current = currentOrg?.id || null
      dispatch({ type: 'LOAD_CLIENTS', payload: transformedClients })
    } catch (error) {
      console.error('[AssessmentContext] Error fetching clients:', error)
      // If first attempt fails, retry with session validation
      if (retryCount === 0) {
        fetchInProgress.current = false
        const delay = 1000
        retryTimeoutRef.current = setTimeout(() => fetchClients(1), delay)
        return
      }
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      fetchInProgress.current = false
    }
  }, [user, profile?.role, authLoading, orgLoading, currentOrg?.id])

  useEffect(() => {
    // Reset guards when org changes to ensure refetch
    fetchInProgress.current = false
    fetchClients()

    // Cleanup retry timeout on unmount or when dependencies change
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [fetchClients])

  // Safety fallback: if still loading after 15s, reset and re-attempt fetch
  useEffect(() => {
    if (!state.loading) return

    const loadingTimeout = setTimeout(() => {
      if (state.loading) {
        console.warn('[AssessmentContext] Loading timeout reached, re-attempting fetch')
        fetchInProgress.current = false
        lastFetchedUserId.current = null
        fetchClients(5) // Skip getUser validation on this retry
      }
    }, 15000)

    return () => clearTimeout(loadingTimeout)
  }, [state.loading, fetchClients])

  // Add a new client
  const addClient = async (clientData) => {
    if (!user) return null

    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          user_id: user.id,
          name: clientData.name,
          industry: clientData.industry || null,
          industry_sub_category: clientData.industrySubCategory || null,
          contact_name: clientData.contactName || null,
          contact_email: clientData.contactEmail || null,
          secondary_contact_name: clientData.secondaryContactName || null,
          secondary_contact_email: clientData.secondaryContactEmail || null,
          notes: clientData.notes || null,
          staffing_size: clientData.staffingSize || null,
          annual_revenue: clientData.annualRevenue || null,
          it_team_size: clientData.itTeamSize || null,
          geographical_presence: clientData.geographicalPresence || null,
          domain: clientData.domain || null,
          enrichment_context: clientData.enrichmentContext || null
        })
        .select()
        .single()

      if (error) throw error

      const newClient = {
        id: data.id,
        name: data.name,
        industry: data.industry,
        industrySubCategory: data.industry_sub_category,
        status: data.status || 'discovery',
        contactName: data.contact_name,
        contactEmail: data.contact_email,
        secondaryContactName: data.secondary_contact_name,
        secondaryContactEmail: data.secondary_contact_email,
        notes: data.notes,
        staffingSize: data.staffing_size,
        annualRevenue: data.annual_revenue,
        itTeamSize: data.it_team_size,
        geographicalPresence: data.geographical_presence,
        domain: data.domain || null,
        enrichmentContext: data.enrichment_context || null,
        createdAt: data.created_at,
        assessments: []
      }

      dispatch({ type: 'ADD_CLIENT', payload: newClient })
      logAuditEvent({
        category: 'change',
        action: 'client.created',
        description: `Created client "${newClient.name}"`,
        clientId: newClient.id,
        clientName: newClient.name,
        resourceType: 'client',
        resourceId: newClient.id
      })
      return newClient
    } catch (error) {
      console.error('Error adding client:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      return null
    }
  }

  // Delete a client
  const deleteClient = async (clientId) => {
    const clientToDelete = state.clients.find(c => c.id === clientId)
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId)

      if (error) throw error

      dispatch({ type: 'DELETE_CLIENT', payload: clientId })
      logAuditEvent({
        category: 'change',
        action: 'client.deleted',
        description: `Deleted client "${clientToDelete?.name || clientId}"`,
        clientId,
        clientName: clientToDelete?.name,
        resourceType: 'client',
        resourceId: clientId
      })
    } catch (error) {
      console.error('Error deleting client:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  // Get client by ID
  const getClient = (clientId) => {
    return state.clients.find(c => c.id === clientId) || null
  }

  // Cascade industry change to ALL assessments for a client
  // Updates business scoping (bs-0, bs-0a, rf-1, dt-1) and threat profile
  const cascadeIndustryChange = async (client, oldIndustry, oldSubCategory) => {
    try {
      const allAssessments = client.assessments || []
      if (allAssessments.length === 0) return

      const industry = client.industry
      const subCategory = client.industrySubCategory
      const { getBusinessScopingIndustryValue, getSubCategory } = await import('../shared/data/industries')

      let updatedAssessments = [...allAssessments]

      for (const assessment of allAssessments) {
        const assessmentId = assessment.id

        // Fetch current assessment responses from DB
        const { data: current, error: fetchError } = await supabase
          .from('assessment_responses')
          .select('business_scoping, notes')
          .eq('assessment_id', assessmentId)
          .single()

        if (fetchError || !current) continue

        const businessScoping = current.business_scoping || {}
        const notes = current.notes || {}

        // Update bs-0 (industry selection)
        const industryValue = getBusinessScopingIndustryValue(industry, subCategory)
        if (industryValue) {
          businessScoping['bs-0'] = { value: industryValue, weight: 1.0 }
        }

        // Update bs-0a (sub-industry)
        if (subCategory) {
          const subCat = getSubCategory(industry, subCategory)
          if (subCat) {
            businessScoping['bs-0a'] = { value: subCat.value, label: subCat.label, weight: 1.0 }
          }
        } else {
          delete businessScoping['bs-0a']
        }

        // Update rf-1 (regulatory frameworks) — use reconciled frameworks from client record
        const newFrameworks = client.regulatoryFrameworks || getDefaultFrameworks(industry, subCategory)
        businessScoping['rf-1'] = { value: newFrameworks, weight: 1.0 }

        // Update dt-1 (sensitive data types) — reconcile: remove old auto, add new, keep manual
        const currentDt1 = Array.isArray(businessScoping['dt-1']?.value) ? businessScoping['dt-1'].value : []
        const oldDataTypes = getDefaultDataTypes(oldIndustry, oldSubCategory)
        const newDataTypes = getDefaultDataTypes(industry, subCategory)
        const manualDt1 = currentDt1.filter(d => !oldDataTypes.includes(d))
        const mergedDt1 = [...new Set([...newDataTypes, ...manualDt1])]
        businessScoping['dt-1'] = { value: mergedDt1, weight: 1.0 }

        // Re-recommend threat profile based on new industry
        const recommendation = recommendProfile({ client, businessScoping })
        const oldProfileKey = notes.threatContext?.profileKey
        if (recommendation.profileId && recommendation.profileId !== oldProfileKey) {
          const threatProfiles = (await import('../shared/data/threatProfiles.json')).default
          const newProfile = threatProfiles.profiles.find(p => p.verticalKey === recommendation.profileId)
          if (newProfile) {
            notes.threatContext = {
              ...(notes.threatContext || {}),
              profileKey: newProfile.verticalKey,
              profileName: newProfile.verticalName,
              actorWeights: null,
              vectorWeights: null,
              assetWeights: null
            }
          }
        }

        // Save updated responses to DB
        const { error: saveError } = await supabase
          .from('assessment_responses')
          .update({ business_scoping: businessScoping, notes })
          .eq('assessment_id', assessmentId)

        if (saveError) continue

        // Touch assessment timestamp
        await supabase
          .from('assessments')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', assessmentId)

        // Build updated assessment for local state
        const updatedAssessment = {
          ...assessment,
          updatedAt: new Date().toISOString(),
          responses: {
            ...assessment.responses,
            businessScoping,
            threatContext: notes.threatContext,
            notes
          }
        }
        updatedAssessments = updatedAssessments.map(a =>
          a.id === assessmentId ? updatedAssessment : a
        )

        // Update currentAssessment if it matches
        if (state.currentAssessment?.id === assessmentId) {
          dispatch({
            type: 'UPDATE_CURRENT_ASSESSMENT',
            payload: {
              responses: updatedAssessment.responses
            }
          })
        }
      }

      // Update client with all updated assessments
      const updatedClient = {
        ...client,
        assessments: updatedAssessments
      }
      dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
    } catch (err) {
      console.error('Error cascading industry change:', err)
    }
  }

  // Update client details
  const updateClient = async (clientId, updates) => {
    if (!user) return null

    try {
      // Map frontend field names to database column names
      const dbUpdates = {}
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.industry !== undefined) dbUpdates.industry = updates.industry
      if (updates.industrySubCategory !== undefined) dbUpdates.industry_sub_category = updates.industrySubCategory
      if (updates.status !== undefined) dbUpdates.status = updates.status
      if (updates.contactName !== undefined) dbUpdates.contact_name = updates.contactName
      if (updates.contactEmail !== undefined) dbUpdates.contact_email = updates.contactEmail
      if (updates.secondaryContactName !== undefined) dbUpdates.secondary_contact_name = updates.secondaryContactName
      if (updates.secondaryContactEmail !== undefined) dbUpdates.secondary_contact_email = updates.secondaryContactEmail
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes
      if (updates.staffingSize !== undefined) dbUpdates.staffing_size = updates.staffingSize
      if (updates.annualRevenue !== undefined) dbUpdates.annual_revenue = updates.annualRevenue
      if (updates.itTeamSize !== undefined) dbUpdates.it_team_size = updates.itTeamSize
      if (updates.geographicalPresence !== undefined) dbUpdates.geographical_presence = updates.geographicalPresence
      if (updates.portalSettings !== undefined) dbUpdates.portal_settings = updates.portalSettings
      if (updates.regulatoryFrameworks !== undefined) dbUpdates.regulatory_frameworks = updates.regulatoryFrameworks
      if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl
      if (updates.logoInitials !== undefined) dbUpdates.logo_initials = updates.logoInitials
      if (updates.logoColor !== undefined) dbUpdates.logo_color = updates.logoColor
      if (updates.domain !== undefined) dbUpdates.domain = updates.domain
      if (updates.enrichmentContext !== undefined) dbUpdates.enrichment_context = updates.enrichmentContext

      const { error } = await supabase
        .from('clients')
        .update(dbUpdates)
        .eq('id', clientId)

      if (error) throw error

      // Update local state
      const existingClient = state.clients.find(c => c.id === clientId)
      if (existingClient) {
        const updatedClient = {
          ...existingClient,
          ...updates
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })

        // Build change details: before/after for each updated field
        const changes = {}
        for (const key of Object.keys(updates)) {
          const oldVal = existingClient[key]
          const newVal = updates[key]
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes[key] = { from: oldVal ?? null, to: newVal ?? null }
          }
        }

        logAuditEvent({
          category: 'change',
          action: 'client.updated',
          description: `Updated client "${updatedClient.name}"`,
          clientId,
          clientName: updatedClient.name,
          resourceType: 'client',
          resourceId: clientId,
          metadata: { fields: Object.keys(updates), changes }
        })

        // Cascade industry changes to active assessments
        if (updates.industry !== undefined || updates.industrySubCategory !== undefined) {
          await cascadeIndustryChange(updatedClient, existingClient.industry, existingClient.industrySubCategory)
        }

        return updatedClient
      }
      return null
    } catch (error) {
      console.error('Error updating client:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      return null
    }
  }

  // Create a new assessment
  const createAssessment = async (clientId) => {
    try {
      // Create assessment
      const { data: assessment, error: assessmentError } = await supabase
        .from('assessments')
        .insert({
          client_id: clientId,
          status: 'in_progress',
          current_step: 'business-scoping',
          current_section_index: 0
        })
        .select()
        .single()

      if (assessmentError) throw assessmentError

      // Create empty responses record
      const { error: responsesError } = await supabase
        .from('assessment_responses')
        .insert({
          assessment_id: assessment.id,
          risk_appetite: {},
          business_scoping: {},
          technology_scoping: {},
          maturity_assessment: {},
          notes: {}
        })

      if (responsesError) throw responsesError

      const newAssessment = {
        id: assessment.id,
        clientId: assessment.client_id,
        status: assessment.status,
        currentStep: assessment.current_step,
        currentSection: assessment.current_section_index,
        createdAt: assessment.created_at,
        completedAt: null,
        responses: {
          riskAppetite: {},
          businessScoping: {},
          technologyScoping: {},
          maturityAssessment: {},
          notes: {},
          threatContext: null,
          impactData: null
        },
        scores: null
      }

      // Update local state
      const client = state.clients.find(c => c.id === clientId)
      if (client) {
        const updatedClient = {
          ...client,
          assessments: [...client.assessments, newAssessment]
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
      }

      dispatch({ type: 'SET_CURRENT_ASSESSMENT', payload: newAssessment })
      return newAssessment
    } catch (error) {
      console.error('Error creating assessment:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      return null
    }
  }

  // Get assessment by ID
  const getAssessment = (assessmentId) => {
    for (const client of state.clients) {
      const assessment = client.assessments?.find(a => a.id === assessmentId)
      if (assessment) {
        return { assessment, client }
      }
    }
    return { assessment: null, client: null }
  }

  // Set current assessment
  const setCurrentAssessment = (assessment) => {
    dispatch({ type: 'SET_CURRENT_ASSESSMENT', payload: assessment })
  }

  // Update assessment (step, section)
  const updateAssessment = async (assessmentId, updates) => {
    try {
      const dbUpdates = {}
      if (updates.currentStep !== undefined) dbUpdates.current_step = updates.currentStep
      if (updates.currentSection !== undefined) dbUpdates.current_section_index = updates.currentSection
      if (updates.status !== undefined) dbUpdates.status = updates.status
      if (updates.completedAt !== undefined) dbUpdates.completed_at = updates.completedAt

      if (Object.keys(dbUpdates).length > 0) {
        const { error } = await supabase
          .from('assessments')
          .update(dbUpdates)
          .eq('id', assessmentId)

        if (error) throw error
      }

      dispatch({ type: 'UPDATE_CURRENT_ASSESSMENT', payload: updates })

      // Update in clients list too
      const { client } = getAssessment(assessmentId)
      if (client) {
        const updatedClient = {
          ...client,
          assessments: client.assessments.map(a =>
            a.id === assessmentId ? { ...a, ...updates } : a
          )
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
      }
    } catch (error) {
      console.error('Error updating assessment:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  // Update assessment details (title, dates, status)
  const updateAssessmentDetails = async (assessmentId, updates) => {
    try {
      const dbUpdates = {}
      if (updates.title !== undefined) dbUpdates.title = updates.title
      if (updates.createdAt !== undefined) dbUpdates.created_at = updates.createdAt
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate
      if (updates.status !== undefined) dbUpdates.status = updates.status

      if (Object.keys(dbUpdates).length > 0) {
        const { error } = await supabase
          .from('assessments')
          .update(dbUpdates)
          .eq('id', assessmentId)

        if (error) throw error
      }

      // Update in clients list
      for (const client of state.clients) {
        const assessmentIndex = client.assessments?.findIndex(a => a.id === assessmentId)
        if (assessmentIndex !== -1) {
          const updatedClient = {
            ...client,
            assessments: client.assessments.map(a =>
              a.id === assessmentId ? { ...a, ...updates } : a
            )
          }
          dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
          break
        }
      }

      // Update current assessment if it matches
      if (state.currentAssessment?.id === assessmentId) {
        dispatch({ type: 'UPDATE_CURRENT_ASSESSMENT', payload: updates })
      }
    } catch (error) {
      console.error('Error updating assessment details:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  // Save a response
  const saveResponse = async (assessmentId, section, questionId, response) => {
    try {
      // TIDA data is stored under the notes column as sub-keys
      const tidaSections = ['threatContext', 'impactData']

      if (tidaSections.includes(section)) {
        // Handle TIDA data - store in notes column under section key
        const { data: current, error: fetchError } = await supabase
          .from('assessment_responses')
          .select('notes')
          .eq('assessment_id', assessmentId)
          .single()

        if (fetchError) throw fetchError

        const updatedNotes = {
          ...(current.notes || {}),
          [section]: response // response is the full data object
        }

        const { error: updateError } = await supabase
          .from('assessment_responses')
          .update({ notes: updatedNotes })
          .eq('assessment_id', assessmentId)

        if (updateError) throw updateError

        // Update local state
        if (state.currentAssessment?.id === assessmentId) {
          dispatch({
            type: 'UPDATE_CURRENT_ASSESSMENT',
            payload: {
              responses: {
                ...state.currentAssessment.responses,
                [section]: response,
                notes: updatedNotes
              }
            }
          })
        }

        // Update in clients list
        const { client } = getAssessment(assessmentId)
        if (client) {
          const updatedClient = {
            ...client,
            assessments: client.assessments.map(a => {
              if (a.id === assessmentId) {
                return {
                  ...a,
                  updatedAt: new Date().toISOString(),
                  responses: {
                    ...a.responses,
                    [section]: response,
                    notes: updatedNotes
                  }
                }
              }
              return a
            })
          }
          dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
        }

        // Touch assessments.updated_at so staleness checks reflect recent activity
        await supabase
          .from('assessments')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', assessmentId)
        return
      }

      // Auto-disable overrides (maturity questions re-enabled by the assessor)
      // are stored under notes.autoDisableOverrides as { [questionId]: true }
      if (section === 'autoDisableOverrides') {
        const { data: current, error: fetchError } = await supabase
          .from('assessment_responses')
          .select('notes')
          .eq('assessment_id', assessmentId)
          .single()

        if (fetchError) throw fetchError

        const currentOverrides = current.notes?.autoDisableOverrides || {}
        let updatedOverrides
        if (response === null || response === false) {
          const { [questionId]: _, ...rest } = currentOverrides
          updatedOverrides = rest
        } else {
          updatedOverrides = { ...currentOverrides, [questionId]: true }
        }

        const updatedNotes = { ...(current.notes || {}), autoDisableOverrides: updatedOverrides }

        const { error: updateError } = await supabase
          .from('assessment_responses')
          .update({ notes: updatedNotes })
          .eq('assessment_id', assessmentId)

        if (updateError) throw updateError

        if (state.currentAssessment?.id === assessmentId) {
          dispatch({
            type: 'UPDATE_CURRENT_ASSESSMENT',
            payload: {
              responses: {
                ...state.currentAssessment.responses,
                notes: updatedNotes
              }
            }
          })
        }

        const { client: overrideClient } = getAssessment(assessmentId)
        if (overrideClient) {
          const updatedClient = {
            ...overrideClient,
            assessments: overrideClient.assessments.map(a => {
              if (a.id === assessmentId) {
                return { ...a, responses: { ...a.responses, notes: updatedNotes } }
              }
              return a
            })
          }
          dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
        }

        await supabase
          .from('assessments')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', assessmentId)
        return
      }

      // Map section names to database columns
      // Flags are stored as a nested object in notes column (like TIDA data)
      if (section === 'flags') {
        const { data: current, error: fetchError } = await supabase
          .from('assessment_responses')
          .select('notes')
          .eq('assessment_id', assessmentId)
          .single()

        if (fetchError) throw fetchError

        const currentFlags = current.notes?.flags || {}
        let updatedFlags
        if (response === null) {
          // Remove flag
          const { [questionId]: _, ...rest } = currentFlags
          updatedFlags = rest
        } else {
          updatedFlags = { ...currentFlags, [questionId]: response }
        }

        const updatedNotes = { ...(current.notes || {}), flags: updatedFlags }

        const { error: updateError } = await supabase
          .from('assessment_responses')
          .update({ notes: updatedNotes })
          .eq('assessment_id', assessmentId)

        if (updateError) throw updateError

        // Update local state
        if (state.currentAssessment?.id === assessmentId) {
          dispatch({
            type: 'UPDATE_CURRENT_ASSESSMENT',
            payload: {
              responses: {
                ...state.currentAssessment.responses,
                flags: updatedFlags,
                notes: updatedNotes
              }
            }
          })
        }

        const { client: flagClient } = getAssessment(assessmentId)
        if (flagClient) {
          const updatedClient = {
            ...flagClient,
            assessments: flagClient.assessments.map(a => {
              if (a.id === assessmentId) {
                return { ...a, responses: { ...a.responses, flags: updatedFlags, notes: updatedNotes } }
              }
              return a
            })
          }
          dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
        }

        await supabase
          .from('assessments')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', assessmentId)
        return
      }

      const sectionMap = {
        riskAppetite: 'risk_appetite',
        businessScoping: 'business_scoping',
        technologyScoping: 'technology_scoping',
        maturityAssessment: 'maturity_assessment',
        notes: 'notes',
        evidence: 'notes'  // Evidence is stored in the notes JSONB column
      }

      const dbColumn = sectionMap[section]
      if (!dbColumn) {
        throw new Error(`Unknown section: ${section}`)
      }

      // Get current responses
      const { data: current, error: fetchError } = await supabase
        .from('assessment_responses')
        .select(dbColumn)
        .eq('assessment_id', assessmentId)
        .single()

      if (fetchError) throw fetchError

      // Update with new response
      const updatedResponses = {
        ...(current[dbColumn] || {}),
        [questionId]: response
      }

      const { error: updateError } = await supabase
        .from('assessment_responses')
        .update({ [dbColumn]: updatedResponses })
        .eq('assessment_id', assessmentId)

      if (updateError) throw updateError

      // Update local state
      if (state.currentAssessment?.id === assessmentId) {
        dispatch({
          type: 'UPDATE_CURRENT_ASSESSMENT',
          payload: {
            responses: {
              ...state.currentAssessment.responses,
              [section]: updatedResponses
            }
          }
        })
      }

      // Update in clients list
      const { client } = getAssessment(assessmentId)
      if (client) {
        const updatedClient = {
          ...client,
          assessments: client.assessments.map(a => {
            if (a.id === assessmentId) {
              return {
                ...a,
                updatedAt: new Date().toISOString(),
                responses: {
                  ...a.responses,
                  [section]: updatedResponses
                }
              }
            }
            return a
          })
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
      }

      // Touch assessments.updated_at so staleness checks reflect recent activity
      await supabase
        .from('assessments')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', assessmentId)
    } catch (error) {
      console.error('Error saving response:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  // Complete an assessment
  const completeAssessment = async (assessmentId, scores) => {
    try {
      // Update assessment status
      const { error: assessmentError } = await supabase
        .from('assessments')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', assessmentId)

      if (assessmentError) throw assessmentError

      // Save scores
      const { error: scoresError } = await supabase
        .from('assessment_scores')
        .upsert({
          assessment_id: assessmentId,
          overall_score: scores.overall?.score,
          maturity_level: scores.overall?.maturityLevel?.label,
          category_scores: scores.maturity?.categoryScores || {},
          risk_appetite_scores: scores.riskAppetite || {},
          business_scoping_scores: scores.businessScoping || {}
        }, { onConflict: 'assessment_id' })

      if (scoresError) throw scoresError

      const updates = {
        status: 'completed',
        completedAt: new Date().toISOString(),
        scores
      }

      dispatch({ type: 'UPDATE_CURRENT_ASSESSMENT', payload: updates })

      // Update in clients list
      const { client } = getAssessment(assessmentId)
      if (client) {
        const updatedClient = {
          ...client,
          assessments: client.assessments.map(a =>
            a.id === assessmentId ? { ...a, ...updates } : a
          )
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
        logAuditEvent({
          category: 'change',
          action: 'assessment.completed',
          description: `Completed assessment for "${client.name}" — score: ${scores.overall?.score ?? 'N/A'}`,
          clientId: client.id,
          clientName: client.name,
          resourceType: 'assessment',
          resourceId: assessmentId,
          metadata: { overallScore: scores.overall?.score, maturityLevel: scores.overall?.maturityLevel?.label }
        })
      }
    } catch (error) {
      console.error('Error completing assessment:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  // Recalculate and save scores for a completed assessment (edit mode)
  const recalculateAndSaveScores = async (assessmentId, scores) => {
    try {
      const { error: scoresError } = await supabase
        .from('assessment_scores')
        .upsert({
          assessment_id: assessmentId,
          overall_score: scores.overall?.score,
          maturity_level: scores.overall?.maturityLevel?.label,
          category_scores: scores.maturity?.categoryScores || {},
          risk_appetite_scores: scores.riskAppetite || {},
          business_scoping_scores: scores.businessScoping || {}
        }, { onConflict: 'assessment_id' })

      if (scoresError) throw scoresError

      // Update local state without changing status/completedAt
      const { client } = getAssessment(assessmentId)
      if (client) {
        const updatedClient = {
          ...client,
          assessments: client.assessments.map(a =>
            a.id === assessmentId ? { ...a, scores } : a
          )
        }
        dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
      }

      return scores
    } catch (error) {
      console.error('[AssessmentContext] recalculateAndSaveScores error:', error)
      throw error
    }
  }

  // Delete an assessment
  const deleteAssessment = async (assessmentId) => {
    try {
      const { error } = await supabase
        .from('assessments')
        .delete()
        .eq('id', assessmentId)

      if (error) throw error

      // Update local state
      if (state.currentAssessment?.id === assessmentId) {
        dispatch({ type: 'SET_CURRENT_ASSESSMENT', payload: null })
      }

      // Update in clients list
      for (const client of state.clients) {
        if (client.assessments?.some(a => a.id === assessmentId)) {
          const updatedClient = {
            ...client,
            assessments: client.assessments.filter(a => a.id !== assessmentId)
          }
          dispatch({ type: 'UPDATE_CLIENT', payload: updatedClient })
          break
        }
      }
    } catch (error) {
      console.error('Error deleting assessment:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const value = {
    clients: state.clients,
    currentAssessment: state.currentAssessment,
    loading: state.loading,
    error: state.error,
    addClient,
    deleteClient,
    getClient,
    updateClient,
    createAssessment,
    getAssessment,
    setCurrentAssessment,
    updateAssessment,
    updateAssessmentDetails,
    saveResponse,
    completeAssessment,
    recalculateAndSaveScores,
    deleteAssessment,
    refreshClients: () => {
      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
      // Reset the guard to allow a forced refresh
      lastFetchedUserId.current = null
      fetchClients(0)
    }
  }

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  )
}

export function useAssessment() {
  const context = useContext(AssessmentContext)
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider')
  }
  return context
}
